import Link from 'next/link';
import { captureException } from '@sentry/nextjs';
import axios from 'axios';
import { apolloClient, apolloClientSSR } from 'graphql/apollo-client';
import { BRANDS, CATEGORIES } from 'graphql/queries';
import { CLIENT } from 'global/constants';
import getAlphabet from 'utils/getAlphabet';
import useScrollRestoration from 'hooks/UI/useScrollRestoration';
import SEO from '@/SEO';
import AlphabetCarousel from '@/Brands/AlphabetSwiper';
import Image from '@/UI/Image';
import Layout from '@/UI/Layout';
import { Container } from '@/UI/Layout/Container';
import { SectionTitleSmall } from '@/UI/Primitives/Headings';
import { FlexBox } from '@/UI/Primitives/FlexBox';
import {
  BrandsContainer,
  Alphabet,
  BrandsByLetter,
  BrandsBox,
  SingleBrand
} from '@/Brands/styles';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';

export default function Brands({ brands, categories }) {
  const { t } = useTranslation();

  // Add safety checks
  if (!brands || !Array.isArray(brands)) {
    return (
      <Layout categories={categories}>
        <SEO
          seo={{
            title: 'Brands - Baltbaby',
            description: 'Baltbaby e-poes on suur valik kõrgelt hinnatud beebi- ja lastekaupade brände.'
          }}
        />
        <BrandsContainer>
          <Container>
            <SectionTitleSmall>Brändid</SectionTitleSmall>
            <p>Brände ei ole hetkel saadaval</p>
          </Container>
        </BrandsContainer>
      </Layout>
    );
  }

  const groupBrands = brands.reduce((alpha, brand) => {
    // Safety checks
    if (!brand?.value || typeof brand.value !== 'string' || brand.value.length === 0) {
      return alpha;
    }

    const group = brand.value[0].toLowerCase();
    
    if (!alpha[group]) {
      alpha[group] = { group, children: [brand] };
    } else {
      alpha[group].children.push(brand);
    }

    return alpha;
  }, {}); // ← Fixed: object instead of array

  const brandsOfAlpha = alpha => {
    if (!groupBrands || !alpha) return null;
    return groupBrands[alpha.toLowerCase()];
  };

  const abc = getAlphabet();
  const alphabet = abc.filter(alpha => {
    const brandsGroup = brandsOfAlpha(alpha);
    return brandsGroup && brandsGroup.children.some(item => 
      item.product_quantity && item.product_quantity > 0
    );
  });

  const scrollToAlpha = alpha => {
    const element = document.getElementById(alpha);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  // Restore scroll position when returning from a specific brand
  // useScrollRestoration({ data: brands });

  return (
    <Layout categories={categories}>
      <SEO
        seo={{
          title: t('header.brands') + ' - Baltbaby',
          description: 'Baltbaby e-poes on suur valik kõrgelt hinnatud beebi- ja lastekaupade brände. Thule, Easywalker, Baby Jogger, Maxi-Cosi ja palju muud.'
        }}
      />

      <BrandsContainer>
        <Container>
          <SectionTitleSmall>{t('header.brands')}</SectionTitleSmall>
          
          {/* Mobile alphabet */}
          <AlphabetCarousel />
          
          {/* Desktop alphabet */}
          <Alphabet>
            <FlexBox>
              {alphabet?.map(alpha => (
                <span
                  key={alpha}
                  role="button"
                  onClick={() => scrollToAlpha(alpha)}
                  style={{ cursor: 'pointer' }}
                >
                  {alpha}
                </span>
              ))}
            </FlexBox>
          </Alphabet>
          
          <BrandsByLetter>
            {alphabet?.map(alpha => {
              const brandsGroup = brandsOfAlpha(alpha);
              
              if (!brandsGroup?.children) return null;
              
              return (
                <BrandsBox
                  key={alpha}
                  id={alpha}
                  p={{ _: '30px 0 0 0', desktop: '20px 0' }}
                >
                  <span>{alpha}</span>
                  <FlexBox
                    display="grid"
                    gridTemplateColumns={{
                      _: 'repeat(2, 1fr)',
                      mobile: 'repeat(2, 1fr)',
                      sm_tablet: 'repeat(4, 1fr)',
                      lg_tablet: 'repeat(6, 1fr)',
                      desktop: 'repeat(7, 1fr)'
                    }}
                    rowGap="2rem"
                    maxW={{ _: '100%', desktop: 1000 }}
                  >
                    {brandsGroup.children
                      .filter(item => item?.product_quantity > 0)
                      .map(brand => {
                        if (!brand?.url_key || !brand?.value) return null;
                        
                        return (
                          <SingleBrand key={brand.url_key}>
                            <Link href={`/brands/${brand.url_key}?id=${brand.option_id}`}>
                              <a>
                                <Image
                                  src={brand.image}
                                  alt={brand.value}
                                  width={92}
                                  height={92}
                                />
                              </a>
                            </Link>
                            <p>
                              {brand.product_quantity}{' '}
                              {brand.product_quantity > 1 ? 'toodet' : 'toode'}
                            </p>
                          </SingleBrand>
                        );
                      })}
                  </FlexBox>
                </BrandsBox>
              );
            })}
          </BrandsByLetter>
        </Container>
      </BrandsContainer>
    </Layout>
  );
}

export async function getStaticProps(context) {
  try {
    const query = (query, variables) => {
      return apolloClientSSR(context.locale).query({
        query,
        variables,
        context: {
          fetchOptions: { method: 'GET' }
        }
      });
    };

    const categoriesPromise = query(CATEGORIES, { ids: '2' });
    const brandsPromise = apolloClient.query({
      query: BRANDS,
      context: { fetchOptions: { method: 'GET' } }
    });

    const [brandsData, categories] = await Promise.all([
      brandsPromise,
      categoriesPromise
    ]);

    const qlBrands = brandsData?.data?.mpbrand?.items;
    let brands = [];

    if (qlBrands && Array.isArray(qlBrands)) {
      try {
        const response = await axios.get(`${CLIENT}/api/brands`);
        
        if (response.data && Array.isArray(response.data)) {
          brands = qlBrands.map(brand => {
            const matchingItem = response.data.find(item => item.value === brand.value);
            return {
              ...brand,
              product_quantity: matchingItem?.product_quantity || 0
            };
          });
        } else {
          // Fallback: use brands without product quantities
          brands = qlBrands.map(brand => ({
            ...brand,
            product_quantity: 0
          }));
        }
      } catch (apiError) {
        captureException(apiError);
        console.error('API brands error:', apiError);
        
        // Fallback: use GraphQL brands without quantities
        brands = qlBrands.map(brand => ({
          ...brand,
          product_quantity: 0
        }));
      }
    }

    return {
      props: {
        brands: brands || [],
        categories: categories?.data?.categoryList?.[0]?.children || [],
        ...(await serverSideTranslations(context.locale))
      },
      revalidate: 100
    };
  } catch (error) {
    captureException(error);
    console.error('getStaticProps error:', error);
    
    // Return minimal props to prevent blank page
    return {
      props: {
        brands: [],
        categories: [],
        ...(await serverSideTranslations(context.locale))
      },
      revalidate: 100
    };
  }
}